import {
  isTouchFirstDevice,
  resolveSurfaceForEnvironment,
  type PointerTraits,
  type Surface,
} from "./breakpoint";

export const SURFACE_MODE_STORAGE_KEY = "quartel-surface-mode";

export type SurfaceMode = Surface | "auto";

export type ResponsiveShellInput = {
  readonly mode: SurfaceMode;
  readonly viewportWidth: number;
  readonly userAgent?: string | undefined;
  // 04/07: primary-pointer/touch traits — a touch-first device pins mobile
  // regardless of width or stored mode (Android "Site para computador" spoofs
  // a desktop UA with a wide layout viewport). Read via deviceTraits.ts.
  readonly pointer?: PointerTraits | undefined;
  // #216 kill switch: when the operator hasn't enabled the desktop shell, the
  // auto-responsive `/` surface stays mobile-only regardless of viewport, UA,
  // or an explicit `mode === "desktop"` preference. Sourced from
  // features/desktop/desktop-shell-flag.ts via useResponsiveSurface.
  readonly desktopEnabled: boolean;
};

type SurfaceModeStorage = Pick<Storage, "getItem" | "setItem">;

export function isSurfaceMode(value: string | null): value is SurfaceMode {
  const known = value === "auto" || value === "mobile";
  return known || value === "desktop";
}

export function resolveResponsiveShell(input: ResponsiveShellInput): Surface {
  // #216 kill switch wins over everything: viewport, UA, AND an explicit
  // `mode === "desktop"`. With the flag off the wide shell does not exist on
  // `/`, so forcing desktop would land on a broken surface.
  if (!input.desktopEnabled) return "mobile";
  // 04/07 (Android sem cache caiu no shell desktop): dispositivo touch-first
  // é SEMPRE mobile — vence inclusive um mode="desktop" persistido, como o
  // legacy (o shell largo não é operável no dedo). Desktop só com pointer
  // fino/UA desktop + largura.
  if (isTouchFirstDevice(input.userAgent, input.pointer)) return "mobile";
  if (input.mode !== "auto") return input.mode;
  const env = {
    viewportWidth: input.viewportWidth,
    userAgent: input.userAgent,
    pointer: input.pointer,
  };
  return resolveSurfaceForEnvironment(env);
}

export function readStoredSurfaceMode(
  storage: Pick<SurfaceModeStorage, "getItem">,
): SurfaceMode {
  const stored = storage.getItem(SURFACE_MODE_STORAGE_KEY);
  if (isSurfaceMode(stored)) return stored;
  return "auto";
}

/** Persist the explicit choice. Swallows quota / private-mode failures —
 * mirrors persistThemePreference (theme-core.ts): the SurfaceModeProvider
 * persist-effect runs without user action on first load, so a throwing
 * setItem would crash the app into AppErrorBoundary in private browsing. */
export function persistSurfaceMode(
  storage: Pick<SurfaceModeStorage, "setItem">,
  mode: SurfaceMode,
): void {
  try {
    storage.setItem(SURFACE_MODE_STORAGE_KEY, mode);
  } catch {
    // storage quota exceeded or private-browsing restriction — ignore
  }
}
